import { upperFirst } from 'lodash';

import {
  DEFAULT_SEARCH_QUERY,
  DISMAX_FIELDS,
  EDISMAX_FIELDS,
  Q_FIELDS,
  SUGGEST_FIELDS,
  HIGHLIGHT_FIELDS,
} from '../../../constants';

/**
 * Copies object property value to a new property, and removes the old property.
 * @param {object} obj - Object to manipulate.
 * @param {string} newKey - Name of the new property to create.
 * @param {string} oldKey - Name of the old property to take the value from and then delete
 */
const updateFormObject = (obj, newKey, oldKey) => {
  obj[newKey] = obj[oldKey];
  delete obj[oldKey];
};

/**
 * Removes an empty fields in a form object.
 * @param {Array.<string>} fieldList - List of field names to check for blank values.
 * @param {object} searchForm - Object that hosts the fields held within the fieldList array.
 */
const removeBlankFields = (fieldList, searchForm) => {
  fieldList.forEach((field) => {
    searchForm[field] === '' && delete searchForm[field];
  });
};

/**
 * Returns a value if it exists in the form
 * @param {object} form - Object that hosts the values to check.
 * @returns the value in a key/value pair, if the value is not falsy.
 */
export const removeUnusedValues = (form) => {
  return JSON.parse(
    JSON.stringify(form, (key, value) => {
      if (value) {
        return value;
      }
    })
  );
};

/**
 * Splits a string by the whitespace or commas within the string.
 * @param {string} field - String to split into an array.
 * @returns An array of split strings, or an empty array.
 */
const stringToArray = (field) => (field ? field.split(/\s+|,\s+|,/g) : []);

/**
 * Generates the solr query field values from a form.
 * @param {object} queryData - Object containing the query data to examine.
 * @returns An object of query data that contains the title, body, image,
 * and url values if they exist in addition to the mandatory uniqueKey and score,
 * joined into a single string and placed in the fields property of the query Data
 */
const assignFlValue = (queryData) => {
  const fieldValues = [queryData.settings.query.uniqueKey, 'score'];
  const { title, body, image, url } = queryData.settings.query.display;

  if (title) fieldValues.push(title);
  if (body) fieldValues.push(body);
  if (image) fieldValues.push(image);
  if (url) fieldValues.push(url);

  queryData.settings.query.jsonQuery.fields.push(fieldValues.join(','));

  return queryData;
};

/**
 * Helps to format form data into a query object for solr.
 * Updates the default field value or sets it to undefined.
 * Updates the query to *:* if blank.
 * @param {object} queryData - Object containing the query data.
 * @returns The query data with the updated df and query values.
 */
const cleanQueryData = (queryData) => {
  const jsonQuery = queryData.settings.query.jsonQuery;

  const cleanedDf = jsonQuery.params.df || undefined;
  const cleanedQ = jsonQuery.query || DEFAULT_SEARCH_QUERY;

  jsonQuery.params.df = cleanedDf;
  jsonQuery.query = cleanedQ;

  return queryData;
};

/**
 * Runs form data through a sequence of functions to transform it
 * into query data appropriate for Solr search requests.
 * @param {object} queryData - Query data to tranform.
 * @returns The updated query data.
 */
export const cleanAndAssignQueryData = (queryData) => {
  return assignFlValue(cleanQueryData(queryData));
};

/**
 * Transforms form and query data so that it can be used in the standalone search webapp.
 * @param {string} baseUrl - The url to point make solr requests to.
 * @param {object} form - The form containing all of the query parameters for solr.
 * @param {Array.<string>} filterQueries - The filterQueries array to pass to the webapp.
 * @param {Array.<string>} facetQueries - The facetQueries array to pass to the webapp.
 * @returns the request payload to be sent to the backend to help create the webapp.
 */
export const formatForWebApp = (baseUrl, form, filterQueries, facetQueries) => {
  const cleanedForm = cleanQueryData(form);

  const query = cleanedForm.settings.query;
  const formatFacetField = stringToArray(query.jsonQuery.params.facetField);

  const suggest = cleanedForm.settings.suggest;
  updateFormObject(suggest, 'qt', 'requestHandler');
  SUGGEST_FIELDS.map((field) =>
    updateFormObject(suggest, `suggest${upperFirst(field)}`, field)
  );
  suggest.suggestDictionary = stringToArray(suggest.suggestDictionary);

  if (query.jsonQuery.params.dense) {
    updateFormObject(query.jsonQuery.params, 'ksDense', 'dense');
    updateFormObject(query.jsonQuery.params, 'ksDenseField', 'denseField');
    updateFormObject(query.jsonQuery.params, 'ksDenseK', 'denseTopK');
    updateFormObject(query.jsonQuery.params, 'ksDenseModel', 'denseModel');
  }

  const webAppPayload = {
    ...query.jsonQuery.params,
    titleField: query.display.title,
    bodyField: query.display.body,
    imageField: query.display.image,
    urlField: query.display.url,
    rawQueryParameters: query.rawQueryParams,
    baseUrl: baseUrl,
    suggestBaseUrl: baseUrl,
    idField: query.uniqueKey,
    qt: query.requestHandler,
    fq: filterQueries,
    facetField: formatFacetField,
    facetQuery: facetQueries,
    sort: query.jsonQuery.sort,
    collection: query.collection,
    ...suggest,
  };

  Q_FIELDS.map((field) =>
    updateFormObject(webAppPayload, `q${upperFirst(field)}`, field)
  );
  HIGHLIGHT_FIELDS.map((field) =>
    updateFormObject(webAppPayload, `hl${upperFirst(field)}`, field)
  );

  removeBlankFields(DISMAX_FIELDS, webAppPayload);
  removeBlankFields(EDISMAX_FIELDS, webAppPayload);

  return webAppPayload;
};

// Query formatting functions
// helper functions followed by full query formatting function

/**
 * Updates the form values for facetting fields to be used for a Solr query.
 * @param {object} formParams - Object containing the form data to be updated.
 * @param {*} facetQueryChips - List of facet queries to be added to the formParams.
 */
const facetParamsToQueryFormat = (formParams, facetQueryChips) => {
  if (formParams.facet) {
    formParams['facet.query'] = facetQueryChips;
    if (formParams.facetField) {
      formParams['facet.field'] = formParams.facetField.split(/\s+|,\s+|,/g);
    }
  }
};

/**
 * Updates the form values for highlighting fields to be used for a Solr query.
 * @param {object} formParams - Object containing the form data to be updated.
 * This function doesn't return anything, but updates the formParams to display
 * the highlighting fields in a way that solr expects. This is brought on by the
 * limitations of react-hook-form. There is no support for field names that have periods in them.
 * For this reason, the form object has to be transformed before it is sent to the backend
 * to match what solr expects.
 */
const highlightParamsToQueryFormat = (formParams) => {
  if (formParams.hl) {
    HIGHLIGHT_FIELDS.map((field) =>
      updateFormObject(formParams, `hl.${field}`, field)
    );
  }
};

/**
 * Updates the form values for query fields to be used for a Solr query.
 * @param {object} params - Object containing the form data to be updated.
 * This function doesn't return anything, but updates the formParams to display
 * the query fields in a way that solr expects. This is brought on by the
 * limitations of react-hook-form. There is no support for field names that have periods in them.
 * For this reason, the form object has to be transformed before it is sent to the backend
 * to match what solr expects.
 */
const qFieldsToQueryFormat = (params) => {
  Q_FIELDS.map((field) => updateFormObject(params, `q.${field}`, field));
};

/**
 * Updates the form values for semantic search fields to be used for a Solr query.
 * @param {object} params - Object containing the form data to be updated.
 * This function doesn't return anything, but updates the params to display
 * the ks fields in a way that solr expects. This is brought on by the
 * limitations of react-hook-form. There is no support for field names that have periods in them.
 * For this reason, the form object has to be transformed before it is sent to the backend
 * to match what solr expects.
 */
const semanticParamsToQueryFormat = (formParams) => {
  if (formParams.dense) {
    updateFormObject(formParams, '_ks.dense', 'dense');
    updateFormObject(formParams, '_ks.dense.k', 'denseTopK');
    updateFormObject(formParams, '_ks.dense.field', 'denseField');
  }
};

/**
 * Updates the form values for suggestor dictionaries to be used for a Solr query.
 * @param {object} form - Object containing the form data to be updated.
 * This function doesn't return anything, but updates the dictionaries by splitting the string
 * from the 'Dictionaries' field in the search form into an array to be processed by Solr.
 * If there are no dictionaries, it simply creates a blank array.
 */
const suggestDictionaryToQueryFormat = (form) => {
  if (form.settings.suggest.dictionary) {
    form.settings.suggest.dictionary =
      form.settings.suggest.dictionary.split(/\s+|,\s+|,/g);
  } else {
    form.settings.suggest.dictionary = [];
  }
};

/**
 * Converts the form object for a search into the query object that Solr expects by running it through a multitude of transformation functions.
 * @param {object} form - The search form.
 * @param {Array.<string>} fqChips - Array of filter queries to be added to the Solr request.
 * @param {*} facetQueryChips - Array of the facet queries to be added to the Solr request.
 * @returns A query object updated to be processed by Solr.
 */
export const convertFormToQuery = (form, fqChips, facetQueryChips) => {
  const jsonQuery = form.settings.query.jsonQuery;
  const params = jsonQuery.params;

  jsonQuery.filter = fqChips;
  jsonQuery.fields = [];
  jsonQuery.limit = 10;
  jsonQuery.offset = 0;

  facetParamsToQueryFormat(params, facetQueryChips);
  highlightParamsToQueryFormat(params);
  qFieldsToQueryFormat(params);
  semanticParamsToQueryFormat(params);
  suggestDictionaryToQueryFormat(form);

  const cleanedForm = removeUnusedValues(form);
  return cleanedForm;
};

/**
 * A function that transforms a Solr query's facetting parameters into a format that the search form expects.
 * @param {object} params - The query parameters to check the facetting fields for and update.
 * This doesn't return anything but creates and updates the properties the form expects and deletes the old query value.
 */
const facetParamsToFormFormat = (params) => {
  updateFormObject(params, 'facetField', 'facet.field');
  if (params.facetField) {
    params.facetField = params.facetField.join(', ');
  }
  delete params['facet.query'];
};

/**
 * A function that transforms a Solr query's highlighting parameters into a format that the search form expects.
 * @param {object} params - The query parameters to check the highlighting fields for and update.
 * This doesn't return anything but creates and updates the properties the form expects and deletes the old query value.
 */
const highlightParamsToFormFormat = (params) => {
  HIGHLIGHT_FIELDS.map((field) =>
    updateFormObject(params, field, `hl.${field}`)
  );
};

/**
 * A function that transforms a Solr query's query parameters into a format that the search form expects.
 * @param {object} params - The query parameters to check the query fields for and update.
 * This doesn't return anything but creates and updates the properties the form expects and deletes the old query value.
 */
const qFieldsToFormFormat = (params) => {
  Q_FIELDS.map((field) => updateFormObject(params, field, `q.${field}`));
};

/**
 * A function that transforms a Solr query's semantic parameters into a format that the search form expects.
 * @param {object} params - The query parameters to check the semantic fields for and update.
 * This doesn't return anything but creates and updates the properties the form expects and deletes the old query value.
 */
const semanticParamsToFormFormat = (params) => {
  updateFormObject(params, 'dense', '_ks.dense');
  updateFormObject(params, 'denseTopK', '_ks.dense.k');
  updateFormObject(params, 'denseField', '_ks.dense.field');
};

/**
 * A function that transforms a Solr query's suggestion dictionary parameters into a format that the search form expects.
 * @param {object} query - The query parameters to check the suggest dictionary field for and update.
 * This doesn't return anything but creates and updates the properties the form expects, by joining them from an array into a string.
 */
const suggestDictionaryToFormFormat = (query) => {
  if (query.settings.suggest.dictionary) {
    query.settings.suggest.dictionary =
      query.settings.suggest.dictionary.join(', ');
  }
};

// This is used within a larger form conversion function in the SearchForm.js component.
/**
 * A function that runs the Solr query through a series of transformation to be consumed by the form.
 * @param {object} query - The query object.
 * This doesn't return anything, but updates the query to be correctly consumed by the form.
 */
export const convertToForm = (query) => {
  const queryParams = query.settings.query.jsonQuery.params;

  facetParamsToFormFormat(queryParams);
  highlightParamsToFormFormat(queryParams);
  qFieldsToFormFormat(queryParams);
  semanticParamsToFormFormat(queryParams);
  suggestDictionaryToFormFormat(query);
};
